import { isDefined } from "@clipboard-health/util-ts";
import { get, getUseGetQueryKey, useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

interface FetchWorkerTypesParams {
  workplaceId: string;
  showHidden: boolean;
}

const workerTypeSchema = z.object({
  name: z.string(),
  visible: z.boolean(),
  contracted: z.boolean().optional(),
});

export const workerTypesResponseSchema = z.object({
  data: z.object({
    workerTypes: z.array(workerTypeSchema),
    workplaceId: z.string().optional(),
  }),
});

export const calendarSettingFormSchema = z.object({
  workerTypes: z
    .array(workerTypeSchema)
    .refine((workerTypes) => workerTypes.some((workerType) => workerType.visible), {
      message: "At least one workerType must be selected",
    }),
});
export type WorkerTypesResponse = z.infer<typeof workerTypesResponseSchema>;
export type WorkerTypes = WorkerTypesResponse["data"];

function getWorkplaceWorkerTypesUrl(workplaceId: string): string {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/worker-types`;
}

export function createFetchWorkplaceWorkerTypesQueryOptions(params: FetchWorkerTypesParams) {
  const { workplaceId, showHidden } = params;
  const url = getWorkplaceWorkerTypesUrl(workplaceId);
  const queryParams = { showHidden };

  return {
    queryKey: getUseGetQueryKey(url, queryParams),
    queryFn: async () => {
      const response = await get({
        url,
        queryParams,
        responseSchema: workerTypesResponseSchema,
      });

      return response.data;
    },
    staleTime: 60_000,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_WORKER_TYPES_FAILURE,
    },
  };
}

export function useFetchWorkplaceWorkerTypes(
  params: FetchWorkerTypesParams,
  options?: UseGetQueryOptions<WorkerTypesResponse>
): UseQueryResult<WorkerTypesResponse> {
  const { workplaceId, showHidden } = params;
  return useGetQuery({
    url: getWorkplaceWorkerTypesUrl(workplaceId),
    queryParams: {
      showHidden,
    },
    responseSchema: workerTypesResponseSchema,
    refetchOnWindowFocus: false,
    ...options,
    enabled: options?.enabled && isDefined(workplaceId),
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_WORKER_TYPES_FAILURE,
    },
  });
}
